import React, { Fragment, useEffect, useState } from "react";
import { InfoCircleTwoTone } from "@ant-design/icons";
import { Table } from "antd";
import { useNavigate } from "react-router-dom";
import moment from "moment";

const getSortOrder = (tableSorter, columnKey) => {
	if (tableSorter.field !== columnKey) {
		return null;
	}

	return tableSorter.order === "ASC" ? "ascend" : tableSorter.order === "DESC" ? "descend" : null;
};

const formatCreatedOnDate = (value) => {
	if (!value || value === "-") {
		return "-";
	}

	const parsedDate = moment(value, ["M/D/YYYY h.mm.ss A", "M/D/YYYY h:mm:ss A", moment.ISO_8601], true);
	return parsedDate.isValid() ? parsedDate.format("DD/MM/YYYY") : "-";
};

const formatRecordCount = (total, range) => {
	if (!total) {
		return "Showing 0 records";
	}

	return `Showing ${range[0]}-${range[1]} of ${total.toLocaleString()} records`;
};

const KycGroupCompanyTab = ({
	tabTableData,
	isLoading,
	tableSorter,
	OnhandleTableSortColumn,
	selectedGroupCompanyId,
	onOpenAccountTab,
}) => {
	const navigate = useNavigate();
	const [totalRecords, setTotalRecords] = useState(0);
	const [pageNo, setPageNo] = useState(1);

	useEffect(() => {
		setTotalRecords(Number(tabTableData.totalRecords || 0));
		setPageNo(Number(tabTableData.currentPage || 1));
	}, [tabTableData]);

	const handleTableAscDes = (pagination, filters, sorter) => {
		const sorterList = Array.isArray(sorter) ? sorter : sorter ? [sorter] : [];
		const activeSorter =
			sorterList.find((s) => s && s.order) ||
			sorterList.find((s) => {
				const f = s?.field;
				const fieldStr = Array.isArray(f) ? f[0] : f;
				return fieldStr === tableSorter.field || s?.columnKey === tableSorter.field;
			}) ||
			sorterList[0];
		const rawField = activeSorter?.field;
		const sortField =
			rawField !== undefined && rawField !== null && rawField !== ""
				? Array.isArray(rawField)
					? rawField[0]
					: rawField
				: activeSorter?.columnKey ?? tableSorter.field;

		let nextOrder = tableSorter.order;
		if (activeSorter?.order === "ascend") {
			nextOrder = "ASC";
		} else if (activeSorter?.order === "descend") {
			nextOrder = "DESC";
		} else {
			const pageChanged =
				pagination.current !== tabTableData.currentPage ||
				(pagination.pageSize != null &&
					tabTableData.pageSize != null &&
					pagination.pageSize !== tabTableData.pageSize);
			if (!pageChanged && String(sortField) === String(tableSorter.field)) {
				const o = String(tableSorter.order || "").toUpperCase();
				nextOrder = o === "DESC" ? "ASC" : "DESC";
			}
		}

		OnhandleTableSortColumn({
			sortColumn: sortField,
			orderBy: nextOrder,
			currentPage: pagination.current,
			pageSize: pagination.pageSize,
		});
	};

	const handlePrimaryContactClick = (event, record) => {
		event.stopPropagation();
		const contactSapCode = record.primaryContact_SAPCode || record.primaryContactSAPCode || record.sapCode;
		if (!contactSapCode) {
			return;
		}

		navigate(`/kyc/primary-contact/${encodeURIComponent(contactSapCode)}`, {
			state: {
				groupCompanyName: record.ispl_CustomerGroup || record.groupCompany,
				accountName: record.accountName,
				primaryContactName: record.primaryContact || record.primaryContactName,
			},
		});
	};

	const tableColumns = [
		{
			title: "Account Name",
			dataIndex: "accountName",
			key: "accountName",
			sorter: true,
			showSorterTooltip: false,
			width: 220,
			sortOrder: getSortOrder(tableSorter, "accountName"),
			render: (value) => <span>{value || "-"}</span>,
		},
		{
			title: "Account Code",
			dataIndex: "ispl_AccountCode",
			key: "ispl_AccountCode",
			sorter: true,
			showSorterTooltip: false,
			width: 200,
			sortOrder: getSortOrder(tableSorter, "ispl_AccountCode"),
			render: (value) => <span>{value || "-"}</span>,
		},
		{
			title: "SAP Code",
			dataIndex: "sapCode",
			key: "sapCode",
			sorter: true,
			showSorterTooltip: false,
			width: 170,
			sortOrder: getSortOrder(tableSorter, "sapCode"),
			render: (value) => <span>{value || "-"}</span>,
		},
		{
			title: "Legal Name",
			dataIndex: "ispl_LegalName",
			key: "ispl_LegalName",
			sorter: true,
			showSorterTooltip: false,
			width: 200,
			sortOrder: getSortOrder(tableSorter, "ispl_LegalName"),
			render: (value) => <span>{value || "-"}</span>,
		},
		{
			title: "KPPL Division New",
			dataIndex: "ispl_KPPLDivisionNew",
			key: "ispl_KPPLDivisionNew",
			sorter: true,
			showSorterTooltip: false,
			width: 220,
			sortOrder: getSortOrder(tableSorter, "ispl_KPPLDivisionNew"),
			render: (value) => <span>{value || "-"}</span>,
		},
		{
			title: "Sales District",
			dataIndex: "ispl_SalesDistrict",
			key: "ispl_SalesDistrict",
			sorter: true,
			showSorterTooltip: false,
			width: 180,
			sortOrder: getSortOrder(tableSorter, "ispl_SalesDistrict"),
			render: (value) => <span>{value || "-"}</span>,
		},
		{
			title: "Sales Office",
			dataIndex: "ispl_SalesOffice",
			key: "ispl_SalesOffice",
			sorter: true,
			showSorterTooltip: false,
			width: 180,
			sortOrder: getSortOrder(tableSorter, "ispl_SalesOffice"),
			render: (value) => <span>{value || "-"}</span>,
		},
		{
			title: "Sales Type (Price Group)",
			dataIndex: "ispl_PriceGroupSalestype",
			key: "ispl_PriceGroupSalestype",
			sorter: true,
			showSorterTooltip: false,
			width: 140,
			sortOrder: getSortOrder(tableSorter, "ispl_PriceGroupSalestype"),
			render: (value) => <span>{value || "-"}</span>,
		},
		{
			title: "Distribution Channel",
			dataIndex: "ispl_DistributionChannel",
			key: "ispl_DistributionChannel",
			sorter: true,
			showSorterTooltip: false,
			width: 160,
			sortOrder: getSortOrder(tableSorter, "ispl_DistributionChannel"),
			render: (value) => <span>{value || "-"}</span>,
		},
		{
			title: "Primary Contact",
			dataIndex: "primaryContact",
			key: "primaryContact",
			sorter: true,
			showSorterTooltip: false,
			width: 180,
			sortOrder: getSortOrder(tableSorter, "primaryContact"),
			render: (_, record) => {
				const label = record.primaryContact || record.primaryContactName || "-";
				const contactSapCode = record.primaryContact_SAPCode || record.primaryContactSAPCode || record.sapCode;

				if (!contactSapCode || label === "-") {
					return <span>{label}</span>;
				}

				return (
					<span className="kyc-primary-contact-link" onClick={(event) => handlePrimaryContactClick(event, record)}>
						{label}
					</span>
				);
			},
		},
		{
			title: "Main Phone",
			dataIndex: "telephone1",
			key: "telephone1",
			sorter: true,
			showSorterTooltip: false,
			width: 180,
			sortOrder: getSortOrder(tableSorter, "telephone1"),
			render: (value) => <span>{value || "-"}</span>,
		},
		{
			title: "Customer Group",
			dataIndex: "ispl_CustomerGroup",
			key: "ispl_CustomerGroup",
			sorter: true,
			showSorterTooltip: false,
			width: 180,
			sortOrder: getSortOrder(tableSorter, "ispl_CustomerGroup"),
			render: (value) => <span>{value || "-"}</span>,
		},
		{
			title: "Country",
			dataIndex: "ispl_Country",
			key: "ispl_Country",
			sorter: true,
			showSorterTooltip: false,
			width: 180,
			sortOrder: getSortOrder(tableSorter, "ispl_Country"),
			render: (value) => <span>{value || "-"}</span>,
		},
		{
			title: "Created On",
			dataIndex: "createdOn",
			key: "createdOn",
			sorter: true,
			showSorterTooltip: false,
			width: 180,
			sortOrder: getSortOrder(tableSorter, "createdOn"),
			render: (value) => <span>{formatCreatedOnDate(value)}</span>,
		},
		{
			title: "Owner",
			dataIndex: "ownerid",
			key: "ownerid",
			sorter: true,
			showSorterTooltip: false,
			width: 180,
			sortOrder: getSortOrder(tableSorter, "ownerid"),
			render: (value) => <span>{value || "-"}</span>,
		},
		{
			title: "Action",
			dataIndex: "action",
			key: "action",
			width: 90,
			fixed: "right",
			render: (_, record) => (
				<div
					className="tbl_info_details"
					onClick={(event) => {
						event.stopPropagation();
						onOpenAccountTab?.(record);
					}}
				>
					<InfoCircleTwoTone />
				</div>
			),
		},
	];

	return (
		<Fragment>
			<Table
				bordered
				scroll={{ x: 1550 }}
				className="logistic_tables kyc_logistic_tables"
				dataSource={tabTableData.rows}
				columns={tableColumns}
				sortDirections={["ascend", "descend"]}
				onChange={handleTableAscDes}
				rowKey={(record, index) =>
					record.id || record.groupCompanyId || record.accountCode || record.ispl_AccountCode || index
				}
				rowClassName={(record) => {
					const recordId =
						record.groupcomanyId ||
						record.groupCompanyId ||
						record.id ||
						record.groupCompanyMasterId ||
						record.ispl_GroupCompanyId;

					return String(recordId) === String(selectedGroupCompanyId) ? "kyc-selected-row" : "";
				}}
				pagination={{
					total: totalRecords,
					pageNo: pageNo,
					pageSize: tabTableData.pageSize || 10,
					responsive: true,
					showSizeChanger: false,
					showTotal: formatRecordCount,
					current: pageNo,
					onChange: (pageNum) => setPageNo(pageNum),
				}}
				loading={isLoading}
			/>
		</Fragment>
	);
};

export default KycGroupCompanyTab;
